import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import logoLight from "@/assets/Logo-light.png";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Rendered as a muted "Soon" tag; item is not a real link (no page exists yet). */
  soon?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

interface SidebarProps {
  groups: NavGroup[];
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({
  groups,
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const [location] = useLocation();

  const sidebarContent = (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      <div className="flex h-16 items-center overflow-hidden border-b border-sidebar-border px-4">
        <Link href="/dashboard" className="flex items-center">
          {collapsed ? (
            <img src={logoLight} alt="180Pi" className="mx-auto h-7 w-auto" />
          ) : (
            <img src={logoLight} alt="180Pi" className="h-8 w-auto" />
          )}
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {groups.map((group) => (
          <div key={group.label} className="mb-4 last:mb-0">
            {!collapsed && (
              <div className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-sidebar-foreground/40">
                {group.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = location === item.href;

                if (item.soon) {
                  return (
                    <li key={item.href}>
                      <span
                        className={cn(
                          "flex cursor-not-allowed items-center gap-3 rounded-lg py-2.5 text-sm font-medium text-sidebar-foreground/40",
                          collapsed ? "justify-center px-2" : "px-3"
                        )}
                        title={collapsed ? item.label : undefined}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!collapsed && (
                          <span className="flex flex-1 items-center justify-between">
                            {item.label}
                            <span className="rounded-full bg-sidebar-accent px-1.5 py-0.5 text-[10px] font-semibold text-sidebar-foreground/60">
                              Soon
                            </span>
                          </span>
                        )}
                      </span>
                    </li>
                  );
                }

                return (
                  <li key={item.href}>
                    <Link href={item.href} onClick={onMobileClose}>
                      <span
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors",
                          collapsed ? "justify-center px-2" : "px-3",
                          isActive
                            ? "bg-sidebar-primary/50 text-sidebar-accent-foreground ring-1 ring-inset ring-white/10"
                            : "text-sidebar-foreground/70 hover:bg-sidebar-primary/20 hover:text-sidebar-accent-foreground"
                        )}
                        title={collapsed ? item.label : undefined}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {!collapsed && (
                          <span className="flex flex-1 items-center justify-between">
                            {item.label}
                            {isActive && <span className="size-2 rounded-full bg-sidebar-primary-foreground" />}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-2 py-3">
        {!collapsed && (
          <div className="px-3 pb-2 text-xs text-sidebar-foreground/40">Help &amp; Support</div>
        )}
        <button
          onClick={onToggleCollapsed}
          className="flex w-full items-center justify-center rounded-lg p-2 text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      <div className="hidden md:fixed md:inset-y-0 md:left-0 md:z-50 md:flex">{sidebarContent}</div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={onMobileClose} />
      )}

      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 md:hidden transition-transform duration-200",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="relative flex h-full w-60 flex-col">
          <button
            className="absolute right-3 top-3 rounded-lg p-1 text-sidebar-foreground/60 hover:bg-sidebar-accent"
            onClick={onMobileClose}
          >
            <X className="h-4 w-4" />
          </button>
          {sidebarContent}
        </div>
      </div>
    </>
  );
}
